import { DEFAULT_DOOR_COLOR } from "@gettactic/map-builder/src/components/map/defines";
import type {
  Shape,
  WallDoor,
  WallPathShape,
  WallShape,
  WallWindow,
  ZoneShape
} from "@gettactic/map-builder/src/types";
import { getWallDotPosition } from "@gettactic/map-builder/src/utils/bezier";
import React from "react";
import { useEditorSelector } from "../store/EditorStoreContext";
import type { Point } from "../types";

const VERTEX_COLOR = "#2563eb";
const SELECTED_VERTEX_COLOR = "#f59e0b";

export interface ShapeHandlesSvgProps {
  shape: Shape;
  /** Handle radius in world units (kept constant on screen). */
  radius: number;
}

/**
 * Geometry handles of the single selected shape: wall-path vertices,
 * edge midpoints and curvature dots; zone polygon vertices and curvature
 * dots; wall endpoints and curvature dot. Each carries a `data-handle`
 * the select tool dispatches on (`vertex:i`, `midpoint:i`, `curve:i`,
 * `wall-start`, `wall-end`).
 */
export const ShapeHandlesSvg: React.FC<ShapeHandlesSvgProps> = ({
  shape,
  radius
}) => {
  const selectedVertex = useEditorSelector(
    (state) => state.chrome.wallPathSelectedVertexIndex
  );
  if (shape.locked) return null;
  if (shape.type === "wall-path") {
    return (
      <WallPathHandles
        shape={shape}
        radius={radius}
        selectedVertex={selectedVertex}
      />
    );
  }
  if (shape.type === "zone" && (shape.vertices?.length ?? 0) >= 3) {
    return <ZoneHandles shape={shape} radius={radius} />;
  }
  if (shape.type === "wall")
    return <WallHandles shape={shape} radius={radius} />;
  return null;
};

const Dot: React.FC<{
  handle: string;
  point: Point;
  radius: number;
  fill: string;
  stroke: string;
  cursor?: string;
}> = ({ handle, point, radius, fill, stroke, cursor = "move" }) => (
  <circle
    data-handle={handle}
    cx={point.x}
    cy={point.y}
    r={radius}
    fill={fill}
    stroke={stroke}
    strokeWidth={1.5}
    vectorEffect="non-scaling-stroke"
    style={{ cursor }}
  />
);

const CurvatureDot: React.FC<{
  index: number;
  from: Point;
  to: Point;
  curvature: number;
  curvatureCenter: number;
  radius: number;
}> = ({ index, from, to, curvature, curvatureCenter, radius }) => {
  const dot = getWallDotPosition(
    from.x,
    from.y,
    to.x,
    to.y,
    curvature,
    curvatureCenter
  );
  return (
    <>
      {curvature !== 0 ? (
        <line
          x1={from.x + curvatureCenter * (to.x - from.x)}
          y1={from.y + curvatureCenter * (to.y - from.y)}
          x2={dot.x}
          y2={dot.y}
          stroke={VERTEX_COLOR}
          strokeWidth={1}
          strokeDasharray="4 4"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      <Dot
        handle={`curve:${index}`}
        point={dot}
        radius={radius * 0.7}
        fill={VERTEX_COLOR}
        stroke="#ffffff"
      />
    </>
  );
};

/** Draggable marker on each door and window of a straight edge. */
const OpeningHandles: React.FC<{
  edge: number;
  from: Point;
  to: Point;
  doors?: WallDoor[];
  windows?: WallWindow[];
  radius: number;
}> = ({ edge, from, to, doors, windows, radius }) => {
  const along = (position: number): Point => ({
    x: from.x + position * (to.x - from.x),
    y: from.y + position * (to.y - from.y)
  });
  return (
    <>
      {doors?.map((door) => (
        <Dot
          key={door.id}
          handle={`door:${edge}:${door.id}`}
          point={along(door.position)}
          radius={radius * 0.9}
          fill={DEFAULT_DOOR_COLOR}
          stroke="#9ca3af"
          cursor="ew-resize"
        />
      ))}
      {windows?.map((window) => (
        <Dot
          key={window.id}
          handle={`window:${edge}:${window.id}`}
          point={along(window.position)}
          radius={radius * 0.9}
          fill="#bfdbfe"
          stroke="#60a5fa"
          cursor="ew-resize"
        />
      ))}
    </>
  );
};

const WallPathHandles: React.FC<{
  shape: WallPathShape;
  radius: number;
  selectedVertex: number | null;
}> = ({ shape, radius, selectedVertex }) => {
  const { vertices, edges, closed } = shape;
  const edgeCount = closed ? vertices.length : vertices.length - 1;
  return (
    <g pointerEvents="all">
      {Array.from({ length: Math.max(0, edgeCount) }).map((_, index) => {
        const from = vertices[index];
        const to = vertices[(index + 1) % vertices.length];
        const edge = edges[index] || {};
        const length = Math.hypot(to.x - from.x, to.y - from.y);
        const openings = (
          <OpeningHandles
            edge={index}
            from={from}
            to={to}
            doors={edge.doors}
            windows={edge.windows}
            radius={radius}
          />
        );
        if (edge.curvature) {
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: edges are positional
            <React.Fragment key={index}>
              <CurvatureDot
                index={index}
                from={from}
                to={to}
                curvature={edge.curvature}
                curvatureCenter={edge.curvatureCenter ?? 0.5}
                radius={radius}
              />
            </React.Fragment>
          );
        }
        if (length < 5) return null;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: edges are positional
          <React.Fragment key={index}>
            <Dot
              handle={`midpoint:${index}`}
              point={{ x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }}
              radius={radius * 0.8}
              fill="#ffffff"
              stroke={VERTEX_COLOR}
              cursor="copy"
            />
            {/* Openings draw last so a door at the edge's middle stays on top. */}
            {openings}
          </React.Fragment>
        );
      })}
      {vertices.map((vertex, index) => (
        <Dot
          // biome-ignore lint/suspicious/noArrayIndexKey: vertices are positional
          key={index}
          handle={`vertex:${index}`}
          point={vertex}
          radius={radius}
          fill={selectedVertex === index ? SELECTED_VERTEX_COLOR : "#ffffff"}
          stroke={
            selectedVertex === index ? SELECTED_VERTEX_COLOR : VERTEX_COLOR
          }
        />
      ))}
    </g>
  );
};

const ZoneHandles: React.FC<{ shape: ZoneShape; radius: number }> = ({
  shape,
  radius
}) => {
  const vertices = (shape.vertices ?? []).map((vertex) => ({
    x: shape.x + vertex.x,
    y: shape.y + vertex.y
  }));
  const edges = shape.edges ?? [];
  return (
    <g pointerEvents="all">
      {vertices.map((vertex, index) => {
        const to = vertices[(index + 1) % vertices.length];
        const edge = edges[index] || {};
        return (
          <CurvatureDot
            // biome-ignore lint/suspicious/noArrayIndexKey: edges are positional
            key={index}
            index={index}
            from={vertex}
            to={to}
            curvature={edge.curvature || 0}
            curvatureCenter={edge.curvatureCenter ?? 0.5}
            radius={radius}
          />
        );
      })}
      {vertices.map((vertex, index) => (
        <Dot
          // biome-ignore lint/suspicious/noArrayIndexKey: vertices are positional
          key={index}
          handle={`vertex:${index}`}
          point={vertex}
          radius={radius}
          fill="#ffffff"
          stroke={VERTEX_COLOR}
        />
      ))}
    </g>
  );
};

const WallHandles: React.FC<{ shape: WallShape; radius: number }> = ({
  shape,
  radius
}) => (
  <g pointerEvents="all">
    <CurvatureDot
      index={0}
      from={{ x: shape.x, y: shape.y }}
      to={{ x: shape.x2, y: shape.y2 }}
      curvature={shape.curvature || 0}
      curvatureCenter={shape.curvatureCenter ?? 0.5}
      radius={radius}
    />
    <Dot
      handle="wall-start"
      point={{ x: shape.x, y: shape.y }}
      radius={radius}
      fill="#ffffff"
      stroke={VERTEX_COLOR}
    />
    <Dot
      handle="wall-end"
      point={{ x: shape.x2, y: shape.y2 }}
      radius={radius}
      fill="#ffffff"
      stroke={VERTEX_COLOR}
    />
  </g>
);
